// src/services/openrouterApi.ts
import { UserProfile } from '../types';

const OPENROUTER_API_URL = 'https://openrouter.ai/api/v1/chat/completions';

// Add retry logic with exponential backoff
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const callOpenRouterApi = async (
  prompt: string, 
  profile: UserProfile, 
  systemMessage?: string,
  retryCount = 0
): Promise<string> => {
  const apiKey = process.env.REACT_APP_OPENROUTER_API_KEY;
  
  if (!apiKey) {
    throw new Error('OpenRouter API key is not configured. Please check your environment variables.');
  }

  const messages = [];
  
  // Enhanced system message for better math accuracy
  if (systemMessage) {
    messages.push({
      role: 'system' as const,
      content: systemMessage
    });
  } else {
    messages.push({
      role: 'system' as const,
      content: `You are an AI tutor helping a ${profile.level} student from ${profile.country}, ${profile.state}. 
                Please format your responses using Markdown for better readability with headings, bullet points, 
                code blocks, LaTeX for math, and other formatting elements. Maintain a natural, professional tone.
                
                For mathematical questions:
                1. Double-check all calculations and equations for accuracy
                2. Use proper LaTeX formatting: $x^2$ for inline, $$E = mc^2$$ for display
                3. Verify that all mathematical statements are logically consistent
                4. Include step-by-step reasoning for complex problems`
    });
  }
  
  // Add user prompt
  messages.push({
    role: 'user' as const,
    content: prompt
  });

  try {
    const response = await fetch(OPENROUTER_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
        'HTTP-Referer': window.location.origin,
        'X-Title': 'AI Tutor App'
      },
      body: JSON.stringify({
        // Switch to Gemini Pro for better math accuracy
        model: 'google/gemini-2.5-flash',
        messages,
        temperature: 0.7, // Lower temperature for more deterministic math answers
        max_tokens: 8000, // Increased for longer responses (15+ questions)
        stream: false
      })
    });

    if (response.status === 401) {
      throw new Error('Invalid API key. Please check your OpenRouter API key configuration.');
    }
    
    if (response.status === 429) {
      // Rate limit exceeded - implement retry logic
      if (retryCount < 3) {
        const waitTime = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
        console.log(`Rate limit hit. Retrying in ${waitTime/1000} seconds... (attempt ${retryCount + 1}/3)`);
        await sleep(waitTime);
        return callOpenRouterApi(prompt, profile, systemMessage, retryCount + 1);
      }
      throw new Error('Rate limit exceeded. Please wait a moment and try again.');
    }
    
    if (response.status === 503 || response.status === 504) {
      // Service unavailable or gateway timeout - retry
      if (retryCount < 3) {
        const waitTime = Math.pow(2, retryCount) * 1000;
        console.log(`Service temporarily unavailable. Retrying in ${waitTime/1000} seconds... (attempt ${retryCount + 1}/3)`);
        await sleep(waitTime);
        return callOpenRouterApi(prompt, profile, systemMessage, retryCount + 1);
      }
    }
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`OpenRouter API error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    
    if (data.choices && data.choices.length > 0 && data.choices[0].message) {
      const rawResponse = data.choices[0].message.content;
      return rawResponse;
    } else {
      console.error('Invalid response format from OpenRouter:', data);
      throw new Error('Invalid response format from AI service');
    }
  } catch (error) {
    console.error('OpenRouter API call failed:', error);
    throw error;
  }
};

// Optional: Add a batch generation function for large question sets
export const generateExamInBatches = async (
  examConfig: any,
  profile: UserProfile,
  batchSize: number = 10
) => {
  const totalQuestions = examConfig.questionCount;
  const batches = Math.ceil(totalQuestions / batchSize);
  let allQuestions: any[] = [];
  
  for (let i = 0; i < batches; i++) {
    const questionsInBatch = Math.min(batchSize, totalQuestions - (i * batchSize));
    const batchPrompt = `Generate ${questionsInBatch} multiple-choice questions for the following exam configuration. 
                         This is batch ${i + 1} of ${batches}. 
                         Exam Level: ${examConfig.examLevel}
                         ${JSON.stringify(examConfig)}`;
    
    try {
      const response = await callOpenRouterApi(batchPrompt, profile, 
        `You are generating a batch of ${questionsInBatch} MCQ questions. Return ONLY valid JSON.`);
      
      // Parse and merge questions
      const batchQuestions = JSON.parse(response);
      allQuestions = [...allQuestions, ...batchQuestions.questions];
      
      // Small delay between batches to avoid rate limits
      if (i < batches - 1) {
        await sleep(1000);
      }
    } catch (error) {
      console.error(`Error generating batch ${i + 1}:`, error);
      throw error;
    }
  }
  
  return { questions: allQuestions };
};